/**
 * Iter 67ch — Blog post reader page.
 *
 * Route: /blog/:category/:slug
 *
 * Renders a single CMS post with:
 *  - Helmet meta tags (title, description, OG image) for SEO.
 *  - JSON-LD Article schema (publisher, datePublished, image, headline).
 *  - Markdown → HTML via the same lightweight renderer used in the admin
 *    preview (no third-party dep).
 *  - Share buttons (Twitter / Facebook / WhatsApp).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe2, ArrowLeft, Loader2, Share2, Twitter, Facebook, MessageCircle } from "lucide-react";

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _renderMarkdown(src) {
  if (!src) return "";
  let html = src
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^###### (.*)$/gm, "<h6>$1</h6>")
    .replace(/^##### (.*)$/gm, "<h5>$1</h5>")
    .replace(/^#### (.*)$/gm, "<h4>$1</h4>")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>");
  html = html.split(/\n{2,}/).map((p) => /^<(h\d|ul|ol|p)/.test(p) ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
  return html;
}

export default function BlogPostPage() {
  const { slug, category } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false);
    api.get(`/public/blog/${slug}`)
      .then((r) => { if (alive) setPost(r.data); })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Iter 67ch — set <title> + meta description + OG tags on the live page so
  // crawlers and social embeds pick them up without react-helmet-async.
  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    document.title = `${post.meta_title || post.title} · RentalCar.al`;
    const tags = [
      { selector: 'meta[name="description"]', attr: "name", value: "description", content: post.meta_description || post.excerpt || "" },
      { selector: 'meta[property="og:title"]', attr: "property", value: "og:title", content: post.meta_title || post.title },
      { selector: 'meta[property="og:description"]', attr: "property", value: "og:description", content: post.meta_description || post.excerpt || "" },
      { selector: 'meta[property="og:type"]', attr: "property", value: "og:type", content: "article" },
      { selector: 'meta[property="og:image"]', attr: "property", value: "og:image", content: _resolveSrc(post.og_image || post.featured_image) || "" },
      { selector: 'meta[name="twitter:card"]', attr: "name", value: "twitter:card", content: "summary_large_image" },
    ];
    const created = [];
    for (const t of tags) {
      if (!t.content) continue;
      let el = document.head.querySelector(t.selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(t.attr, t.value);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", t.content);
    }
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="py-20 text-center text-neutral-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl mb-3">Post not found</h1>
          <p className="text-neutral-500 mb-6">This article may have moved or been unpublished.</p>
          <Link to={`/blog/${category || "destinations"}`}>
            <Button variant="outline"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to {category === "travel-tips" ? "Travel tips" : "Destinations"}</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(post.title);
  const encodedUrl = encodeURIComponent(shareUrl);

  // JSON-LD Article schema → unlocks rich snippet in Google search.
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "image": _resolveSrc(post.og_image || post.featured_image) || undefined,
    "datePublished": post.published_at || post.created_at,
    "dateModified": post.updated_at,
    "author": { "@type": "Person", "name": post.author_name || "RentalCar.al" },
    "publisher": { "@type": "Organization", "name": "RentalCar.al" },
    "description": post.meta_description || post.excerpt || undefined,
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to={`/blog/${post.category}`} className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 hover:text-blue-600 mb-6">
          <ArrowLeft className="h-3 w-3 mr-1" /> {post.category === "travel-tips" ? "Travel tips" : "Destinations"}
        </Link>

        <article>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {post.country && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.15em] font-bold">
                <Globe2 className="h-3 w-3 mr-0.5" /> {post.country}
              </Badge>
            )}
            {post.is_featured && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Featured</Badge>}
          </div>

          <h1 className="font-display text-4xl lg:text-5xl tracking-tight font-medium leading-tight" data-testid="blog-post-title">{post.title}</h1>

          {post.excerpt && <p className="mt-4 text-lg text-neutral-600 leading-relaxed">{post.excerpt}</p>}

          {post.featured_image && (
            <div className="mt-8 rounded-3xl overflow-hidden bg-neutral-100">
              <img src={_resolveSrc(post.featured_image)} alt={post.title} className="w-full h-auto" />
            </div>
          )}

          <div
            className="mt-10 [&_h1]:font-display [&_h1]:text-3xl [&_h1]:font-medium [&_h1]:mt-8 [&_h1]:mb-4 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-4 [&_p]:leading-relaxed [&_p]:text-neutral-700 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_li]:my-1 [&_a]:text-blue-600 [&_a]:underline [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: _renderMarkdown(post.body_md || "") }}
            data-testid="blog-post-body"
          />

          {/* Share buttons */}
          <div className="mt-12 pt-8 border-t border-border flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">
              <Share2 className="h-3 w-3" /> Share
            </div>
            <Button asChild size="sm" variant="outline" className="rounded-full"><a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer"><Twitter className="h-3.5 w-3.5 mr-1" />Twitter</a></Button>
            <Button asChild size="sm" variant="outline" className="rounded-full"><a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer"><Facebook className="h-3.5 w-3.5 mr-1" />Facebook</a></Button>
            <Button asChild size="sm" variant="outline" className="rounded-full"><a href={`https://wa.me/?text=${shareText}%20${encodedUrl}`} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp</a></Button>
          </div>
        </article>

        {/* eslint-disable-next-line react/no-danger */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </main>
      <Footer />
    </div>
  );
}
